import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Command,
  LayoutDashboard,
  Users,
  Package,
  FileText,
  ShoppingCart,
  Warehouse,
  Calculator,
  Brain,
  Settings,
  UserCircle,
  Store,
  Target,
  FolderKanban,
  X,
  ArrowRight,
  Clock
} from 'lucide-react';

/**
 * Global Search Component (Cmd+K)
 * Quick navigation and search across the entire app
 */
export const GlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // All searchable items
  const searchItems = [
    { id: 'dashboard', label: 'لوحة التحكم', path: '/dashboard', icon: LayoutDashboard, category: 'الصفحات' },
    { id: 'pos', label: 'نقطة البيع', path: '/pos', icon: Store, category: 'الصفحات' },
    { id: 'crm', label: 'إدارة العملاء CRM', path: '/crm', icon: Target, category: 'الصفحات' },
    { id: 'customers', label: 'العملاء', path: '/customers', icon: Users, category: 'الصفحات' },
    { id: 'products', label: 'المنتجات', path: '/products', icon: Package, category: 'الصفحات' },
    { id: 'invoices', label: 'الفواتير', path: '/invoices', icon: FileText, category: 'الصفحات' },
    { id: 'create-invoice', label: 'إنشاء فاتورة جديدة', path: '/invoices/create', icon: FileText, category: 'إجراءات سريعة' },
    { id: 'sales', label: 'المبيعات', path: '/sales', icon: ShoppingCart, category: 'الصفحات' },
    { id: 'purchases', label: 'المشتريات', path: '/purchases', icon: ShoppingCart, category: 'الصفحات' },
    { id: 'warehouses', label: 'المستودعات', path: '/warehouses', icon: Warehouse, category: 'الصفحات' },
    { id: 'accounting', label: 'المحاسبة', path: '/accounting', icon: Calculator, category: 'الصفحات' },
    { id: 'ai-assistant', label: 'المساعد الذكي', path: '/ai-assistant', icon: Brain, category: 'الصفحات' },
    { id: 'projects', label: 'إدارة المشاريع', path: '/projects', icon: FolderKanban, category: 'الصفحات' },
    { id: 'settings', label: 'إعدادات الشركة', path: '/settings', icon: Settings, category: 'الإعدادات' },
    { id: 'profile', label: 'الملف الشخصي', path: '/profile', icon: UserCircle, category: 'الإعدادات' },
    { id: 'users', label: 'إدارة المستخدمين', path: '/users', icon: Users, category: 'الإعدادات' },
    { id: 'zatca', label: 'إعدادات ZATCA', path: '/zatca', icon: FileText, category: 'الإعدادات' },
  ];

  // Filter items based on query
  const filteredItems = query
    ? searchItems.filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.id.toLowerCase().includes(query.toLowerCase())
      )
    : searchItems.slice(0, 6);

  // Group by category
  const groupedItems = filteredItems.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {});

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('recentSearches');
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (e) {}
  }, []);

  // Handle keyboard shortcut (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const items = filteredItems;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          handleSelect(items[selectedIndex]);
        }
        break;
      default:
        break;
    }
  }, [filteredItems, selectedIndex]);

  const handleSelect = (item) => {
    // Save to recent searches
    const newRecent = [item, ...recentSearches.filter(r => r.id !== item.id)].slice(0, 5);
    setRecentSearches(newRecent);
    try {
      localStorage.setItem('recentSearches', JSON.stringify(newRecent));
    } catch (e) {}

    // Navigate
    navigate(item.path);
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg text-sm text-slate-500 dark:text-slate-400 transition-colors"
        aria-label="بحث سريع"
      >
        <Search size={16} />
        <span>بحث سريع...</span>
        <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded text-xs border border-slate-200 dark:border-slate-600">
          <Command size={10} />
          <span>K</span>
        </kbd>
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal */}
      <div
        className="fixed top-[20%] left-1/2 -translate-x-1/2 z-50 w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="global-search-title"
      >
        <h2 id="global-search-title" className="sr-only">بحث سريع</h2>
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <Search className="text-slate-400" size={20} aria-hidden="true" />
          <label htmlFor="global-search-input" className="sr-only">
            بحث سريع عن أي شيء في النظام
          </label>
          <input
            id="global-search-input"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder="ابحث عن صفحة أو إجراء..."
            aria-label="بحث سريع عن أي شيء في النظام"
            className="flex-1 bg-transparent border-0 outline-none text-slate-900 dark:text-white placeholder:text-slate-400"
            autoFocus
          />
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            aria-label="إغلاق البحث"
          >
            <X size={18} className="text-slate-400" aria-hidden="true" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto p-2">
          {/* Recent searches */}
          {!query && recentSearches.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-slate-400 px-3 py-1 flex items-center gap-1">
                <Clock size={12} />
                عمليات البحث الأخيرة
              </p>
              {recentSearches.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <button
                    key={`recent-${item.id}`}
                    onClick={() => handleSelect(item)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-right"
                  >
                    <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                      <Icon size={16} className="text-slate-500" />
                    </div>
                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">{item.label}</span>
                    <ArrowRight size={14} className="text-slate-400" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Grouped results */}
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} className="mb-4">
              <p className="text-xs text-slate-400 px-3 py-1">{category}</p>
              {items.map((item, idx) => {
                const Icon = item.icon;
                const globalIndex = filteredItems.indexOf(item);
                const isSelected = globalIndex === selectedIndex;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-right ${
                      isSelected 
                        ? 'bg-primary/10 dark:bg-primary/20' 
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isSelected 
                        ? 'bg-primary text-white' 
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                    }`}>
                      <Icon size={16} />
                    </div>
                    <span className={`flex-1 text-sm ${
                      isSelected 
                        ? 'text-primary dark:text-primary font-medium' 
                        : 'text-slate-700 dark:text-slate-200'
                    }`}>
                      {item.label}
                    </span>
                    {isSelected && (
                      <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-600 rounded text-xs text-slate-500 dark:text-slate-400">
                        Enter
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          {/* No results */}
          {query && filteredItems.length === 0 && (
            <div className="py-8 text-center text-slate-400">
              <Search size={32} className="mx-auto mb-2 opacity-50" />
              <p>لا توجد نتائج لـ "{query}"</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-xs text-slate-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">↑</kbd>
              <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">↓</kbd>
              للتنقل
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">Enter</kbd>
              للاختيار
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">Esc</kbd>
            للإغلاق
          </span>
        </div>
      </div>
    </>
  );
};

export default GlobalSearch;
